import moment from "moment/moment";

/**
 * Returns the calculated number of months ago from the given date string.
 * @param {string} dateStr - The date string in the format 'YYYY-MM-DD'.
 * @returns {number}
 */
export function monthsAgo(dateStr) {
    const past = moment(dateStr, 'YYYY-MM-DD', /* strict */ true);
    if (!past.isValid()) {
        throw new Error('monthsAgo(): date must be a valid YYYY-MM-DD string');
    }
    const diff = moment().diff(past, 'months');
    return Math.max(0, diff);
}

/**
 * Array of category ranges for the TreeMap graph. These will be the main categories for the TreeMap.
 * @type {Array<{label: string, upper: number}>}
 */
const DEFAULT_CATEGORY_RANGES = [
    {label: "0–6 mo", upper: 6},          // 0  ≤ m < 6
    {label: "6–12 mo", upper: 12},         // 6  ≤ m < 12
    {label: "1–2.5 yr", upper: 30},         // 12 ≤ m < 30
    {label: "2.5–5 yr", upper: 60},         // 30 ≤ m < 60
    {label: "5–8 yr", upper: 96},         // 60 ≤ m < 96
    {label: "8 + yr", upper: Infinity}    // 96 ≤ m
];

/**
 * Helper function to determine the category label based on the number of months ago.
 * @param monthsAgo
 * @param {Array<{label: string, upper: number}>} category_ranges_map - The map of category ranges to use. Defaults to DEFAULT_CATEGORY_RANGES.
 * @returns {string}
 */
function categoryFor(monthsAgo, category_ranges_map = DEFAULT_CATEGORY_RANGES) {
    return category_ranges_map.find(r => monthsAgo < r.upper).label;
}

/**
 * Default raw data processor function. It processes the raw data by aggregating the size against the number of months ago and then build the data table as expected by the TreeMap graph.
 * @param {Array} rawData - The raw data to be processed, expected to be an array of objects with date, bytes, and files properties.
 * @param {string|null} metric - The metric to be used for processing the data, either "Size" or "Size". Defaults to "Size".
 * @returns {{processedData: Array, processedDataTotal: number}}
 */
const defaultRawDataProcessor = (rawData, metric = 'Size') => {
    const localProcessedData = {}

    rawData.forEach((data) => {
        const date = data.date;
        const size = data.bytes;
        const files = data.files;
        const monthsAgoValue = monthsAgo(date);

        (localProcessedData[monthsAgoValue] ??= {bytes: 0, files: 0, category: ""}).bytes += size;
        localProcessedData[monthsAgoValue].files += files;
    });

    Object.entries(localProcessedData).forEach(([monthsStr, valueObj]) => {
        const monthsAgo = Number(monthsStr);      // keys come in as strings
        valueObj.category = categoryFor(monthsAgo);
    });

    const dataTable = [
        ["Category", "Parent Category", "Size"],
        ["Total", null, 0],
        ["0–6 mo", "Total", 0],
        ["6–12 mo", "Total", 0],
        ["1–2.5 yr", "Total", 0],
        ["2.5–5 yr", "Total", 0],
        ["5–8 yr", "Total", 0],
        ["8 + yr", "Total", 0]
    ];
    Object.entries(localProcessedData).forEach(([monthsStr, valueObj]) => {
        const value_to_use = !metric || metric === 'Size' ? valueObj.bytes : valueObj.files;
        dataTable.push([`${monthsStr} mo ago`, valueObj.category, value_to_use]);
    });

    return {processedData: dataTable, processedDataTotal: 0};
};

/**
 * Raw data processor function for the ageOfFilesData variant.
 * @param {Array} rawData - The raw data to be processed, expected to be an array of objects with date, bytes, and files count.
 * @param {string} metric - The metric to be used for processing the data, either "Size" or "Count (Files)".
 * @returns {{processedData: Array, processedDataTotal: number}}
 */
const ageOfFilesDataProcessor = (rawData, metric) => {
    const localProcessedData = {}
    const localCategoryRangesVersion = structuredClone(DEFAULT_CATEGORY_RANGES);

    rawData.forEach((data) => {
        const date = data.date;
        const size = data.bytes;
        const files = data.files;
        const monthsAgoValue = monthsAgo(date);

        (localProcessedData[monthsAgoValue] ??= {bytes: 0, files: 0, parentCategory: "", currentFormattedCategory: ""}).bytes += size;
        localProcessedData[monthsAgoValue].files += files;
    });

    function splitMonths(totalMonths) {
        const years = Math.floor(totalMonths / 12);
        const months = totalMonths % 12;
        return {years, months};
    }

    Object.entries(localProcessedData).forEach(([monthsStr, valueObj]) => {
        const monthsAgo = Number(monthsStr);      // keys come in as strings
        const {years, months} = splitMonths(monthsAgo);
        const restOfMonthsTxt = months > 0 ? ` ${months} mo` : "";
        const yearsText = years > 0 ? `${years} yr` : "";
        const currentFormattedCategory = !years && !months ? "curr mo" : (!years ? `${restOfMonthsTxt} ago` : `${yearsText}${restOfMonthsTxt} ago`);
        valueObj.parentCategory = categoryFor(monthsAgo, localCategoryRangesVersion);
        valueObj.subCategory = currentFormattedCategory;
    });

    const dataTable = !metric || metric === 'Size' ?
        [["Category", "Parent Category", "Size"],
            ["Total", null, 0]] :
        [["Category", "Parent Category", "Files (Count)"],
            ["Total", null, 0]];

    Object.values(localCategoryRangesVersion).forEach((categoryRange) => {
        dataTable.push([categoryRange.label, "Total", null]);
    });

    let totalValue = 0;
    Object.entries(localProcessedData).forEach(([monthsStr, valueObj]) => {
        const value_to_use = !metric || metric === 'Size' ? valueObj.bytes : valueObj.files;
        totalValue += value_to_use;
        dataTable.push([`${monthsStr} mo ago`, valueObj.parentCategory, value_to_use]);
    });

    return {processedData: dataTable, processedDataTotal: totalValue};
};

/**
 * Processes the raw data for the filesTypeData variant.
 * @param {Array} rawData - The raw data to be processed, expected to be an array of objects with company_id, file_type, file_group, bytes, and files properties.
 * @param {string} metric - The metric to be used for processing the data, either "Size" or "Count (Files)". Defaults to "Size".
 * @returns {{processedData: Array, processedDataTotal: number}}
 */
const filesTypeDataProcessor = (rawData, metric) => {
    // expected data per row
    // 	{
    // 		"company_id": 158,
    // 		"file_type": "segd",
    // 		"file_group": "seismic",
    // 		"bytes": 18939523,
    // 		"files": 7
    // 	}
    const accumulatorStartObject = {nestedData: {}, typeToGroupMapping: {}, totalBytes: 0, totalFiles: 0};

    const getGroupUsedKeys = (accumulator) => {
        const groupKeys = new Set();
        Object.entries(accumulator.nestedData).forEach(([parentKey, parentValue]) => {
            groupKeys.add(parentKey);
        });
        return groupKeys;
    };

    const reducingFn = (accumulator, currentValue) => {
        const groupUsedKeys = getGroupUsedKeys(accumulator);

        let fileType = currentValue.file_type ?? "unknown";
        const fileGroup = currentValue.file_group ?? "unknown";
        const bytes = currentValue.bytes ?? 0;
        const files = currentValue.files ?? 0;

        // ensure unique fileType names
        // Possible issues:
        // 1. the name used for current fileType was used before by a different fileGroup
        // 2. in exact this session, the name used by fileType is also used by the current fileGroup (e.g. "file_type": "db", "file_group": "db")
        while (groupUsedKeys.has(fileType) || fileGroup === fileType) {
            fileType = ` ${fileType} `;
        }

        // ensure this fileType wasn't already mapped to a different group
        if (Object.keys(accumulator.typeToGroupMapping).includes(fileType) && accumulator.typeToGroupMapping[fileType] !== fileGroup) {
            return accumulator; // skip this entry
        }

        accumulator.typeToGroupMapping[fileType] = fileGroup;

        const groupDict = accumulator.nestedData[fileGroup] ??= {bytes: 0, files: 0, children: {}};
        groupDict.bytes += bytes;
        groupDict.files += files;
        accumulator.totalBytes += bytes;
        accumulator.totalFiles += files;

        const childDict = groupDict.children[fileType] ??= {bytes: 0, files: 0};
        childDict.bytes += bytes;
        childDict.files += files;

        return accumulator;

    };
    const {nestedData, typeToGroupMapping, totalBytes, totalFiles} = rawData.reduce(reducingFn, accumulatorStartObject);
    const dataTable = !metric || metric === 'Size' ?
        [["Category", "Parent Category", "Size"],
            ["Total", null, 0]] :
        [["Category", "Parent Category", "Files (Count)"],
            ["Total", null, 0]];


    const reducingFn2 = (accumulatedDataTable, [currentParentCateg, currentParentCategObj]) => {
        accumulatedDataTable.push([currentParentCateg, "Total", null]);
        Object.entries(currentParentCategObj.children).forEach(([childCateg, childCategObj]) => {
            const value_to_use = !metric || metric === 'Size' ? childCategObj.bytes : childCategObj.files;
            accumulatedDataTable.push([childCateg, currentParentCateg, value_to_use]);
        });
        return accumulatedDataTable;
    };
    const updatedDataTable = Object.entries(nestedData).reduce(reducingFn2, dataTable);

    const processedDataTotal = !metric || metric === 'Size' ? totalBytes : totalFiles;
    return {processedData: updatedDataTable, processedDataTotal};
};

export const variantRawDataProcessorMapTreeMapGraph = {
    defValue: defaultRawDataProcessor,
    ageOfFilesData: ageOfFilesDataProcessor,
    filesTypeData: filesTypeDataProcessor,
};
